import { DISPLAY, GROUND, INK, LABEL_CLS, mix } from "./theme";
import type { NavLink } from "./types";

export type FooterColumn = { heading: string; links: NavLink[] };

/**
 * Editorial footer — cream ground, hairline top border, wordmark + blurb
 * on the left, link columns on the right, legal row beneath. Calm close
 * after the dark ClosingSection.
 */
export function EditorialFooter({
  wordmark,
  blurb,
  columns,
  legal,
  dark = false,
}: {
  wordmark: string;
  blurb?: string;
  columns: FooterColumn[];
  legal: string;
  dark?: boolean;
}) {
  const background = dark ? "var(--theme-surface-dark)" : GROUND;
  const foreground = dark ? GROUND : INK;
  const body = dark ? mix("--theme-ground", 88) : mix("--theme-ink", 88);
  const metadata = dark ? mix("--theme-ground", 80) : mix("--theme-ink", 80);
  const rule = dark ? mix("--theme-ground", 14) : mix("--theme-ink", 14);

  return (
    <footer
      className="relative"
      style={{
        backgroundColor: background,
        color: foreground,
        borderTop: `1px solid ${rule}`,
      }}
    >
      <div className="mx-auto max-w-screen-xl px-5 py-14 sm:px-6 md:px-14 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_2fr] lg:gap-16">
          <div>
            <span
              style={{ ...DISPLAY, color: foreground }}
              className="text-[1.5rem] font-semibold tracking-[-0.02em]"
            >
              {wordmark}
            </span>
            {blurb && (
              <p
                className="mt-4 max-w-[42ch] text-[0.95rem] leading-[1.6]"
                style={{ color: body }}
              >
                {blurb}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
            {columns.map((col) => (
              <div key={col.heading}>
                <p
                  className={LABEL_CLS}
                  style={{ color: metadata }}
                >
                  {col.heading}
                </p>
                <ul className="mt-4 grid gap-2.5">
                  {col.links.map((l) => (
                    <li key={l.href}>
                      <a
                        href={l.href}
                        className="text-[0.95rem] transition-opacity hover:opacity-70"
                        style={{ color: body }}
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-12 border-t pt-6"
          style={{ borderTopColor: rule }}
        >
          <p className="text-[0.82rem]" style={{ color: metadata }}>
            {legal}
          </p>
        </div>
      </div>
    </footer>
  );
}
